import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Search, Command as CmdIcon, X, ArrowRight } from "lucide-react";

const TYPE_COLOR = {
    "Project": "#fb8b1e", "Client": "#4af6c3", "Vendor": "#0068ff",
    "Invoice (AR)": "#4af6c3", "Bill (AP)": "#ff433d",
    "Tender": "#fb8b1e", "Employee": "#8892B0", "Bank Account": "#0068ff",
};

const QUICK_LINKS = [
    { label: "Command Center", url: "/", type: "Page" },
    { label: "AI CFO Advisor", url: "/ai-advisor", type: "Page" },
    { label: "Scenario Simulator", url: "/simulator", type: "Page" },
    { label: "Intelligence Center", url: "/intelligence", type: "Page" },
    { label: "Bank Reconciliation", url: "/reconciliation", type: "Page" },
    { label: "Cash Book", url: "/cash", type: "Page" },
    { label: "Ratios Wall", url: "/ratios", type: "Page" },
    { label: "₹100 Cr Roadmap", url: "/growth", type: "Page" },
];

export default function CommandPalette({ open, onClose }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [idx, setIdx] = useState(0);
    const inputRef = useRef(null);
    const nav = useNavigate();

    useEffect(() => {
        if (open) {
            setQ(""); setIdx(0);
            setTimeout(() => inputRef.current?.focus(), 30);
        }
    }, [open]);

    useEffect(() => {
        if (!q || q.length < 2) {
            setResults(QUICK_LINKS);
            return;
        }
        const t = setTimeout(() => {
            api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => {
                const filteredQuick = QUICK_LINKS.filter((l) => l.label.toLowerCase().includes(q.toLowerCase()));
                setResults([...filteredQuick, ...r.data.results]);
                setIdx(0);
            }).catch(() => setResults([]));
        }, 150);
        return () => clearTimeout(t);
    }, [q]);

    const go = (item) => {
        nav(item.url);
        onClose();
    };

    const onKey = (e) => {
        if (e.key === "Escape") onClose();
        else if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(results.length - 1, i + 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
        else if (e.key === "Enter" && results[idx]) go(results[idx]);
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-start justify-center pt-24 px-4 bg-[var(--bg-base)]/80 backdrop-blur-sm" onClick={onClose}>
            <div
                data-testid="command-palette"
                className="w-full max-w-2xl bg-panel border border-panel"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-2 border-b border-panel px-3 py-3">
                    <Search size={16} className="text-[var(--text-secondary)]" />
                    <input
                        ref={inputRef}
                        data-testid="cmd-search"
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={onKey}
                        placeholder="Search projects, clients, vendors, invoices, tenders..."
                        className="flex-1 bg-transparent text-[var(--text-primary)] text-sm font-body focus:outline-none placeholder:text-[var(--text-muted)]"
                    />
                    <button data-testid="cmd-close" onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                        <X size={16} />
                    </button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                    {results.length === 0 ? (
                        <div className="p-8 text-center text-[var(--text-secondary)] text-xs font-mono-fin uppercase tracking-wider">No matches</div>
                    ) : (
                        results.map((r, i) => (
                            <button
                                key={i}
                                data-testid={`cmd-result-${i}`}
                                onClick={() => go(r)}
                                onMouseEnter={() => setIdx(i)}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-l-2 ${i === idx ? "bg-[var(--bg-panel-hover)] border-[var(--brand-amber)]" : "border-transparent"}`}
                            >
                                <span className="text-[9px] font-mono-fin uppercase tracking-wider px-1.5 py-0.5 border border-panel shrink-0" style={{ color: TYPE_COLOR[r.type] || "#8892B0", borderColor: TYPE_COLOR[r.type] || "#E2E8F0" }}>{r.type}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-[var(--text-primary)] truncate">{r.label}</div>
                                    {r.sub && <div className="text-[10px] font-mono-fin text-[var(--text-muted)] truncate">{r.sub}</div>}
                                </div>
                                <ArrowRight size={12} className="text-[var(--text-muted)] shrink-0" />
                            </button>
                        ))
                    )}
                </div>
                <div className="border-t border-panel px-3 py-2 flex items-center justify-between text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">
                    <div className="flex items-center gap-3">
                        <span>↑↓ Navigate</span>
                        <span>⏎ Open</span>
                        <span>Esc Close</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <CmdIcon size={10} /> Global Palette
                    </div>
                </div>
            </div>
        </div>
    );
}
